import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, ArrowRight } from "lucide-react";
import { getChapter, getCave, COMPANION, getBand } from "../data/story";
import { useProfile } from "../context/ProfileContext";
import CavePlayer from "../components/caves/CavePlayer";
import RewardPick from "../components/caves/RewardPick";
import CrystalAward from "../components/caves/CrystalAward";
import Cliffhanger from "../components/caves/Cliffhanger";
import { sfx } from "../services/soundfx";
import { speak } from "../services/phonicsAudio";

export default function CavePage() {
  const { chapterId, caveId } = useParams();
  const navigate = useNavigate();
  const { current, loading, saveCave } = useProfile();
  const chapter = getChapter(chapterId);
  const cave = getCave(chapterId, caveId);

  const [phase, setPhase] = useState("intro"); // intro | play | reward | crystal | cliffhanger
  const [stars, setStars] = useState(0);

  useEffect(() => {
    if (!loading && !current) navigate("/profiles");
  }, [current, loading, navigate]);

  useEffect(() => {
    if (cave && phase === "intro") {
      const t = setTimeout(() => speak(cave.beat), 500);
      return () => clearTimeout(t);
    }
  }, [cave, phase]); // eslint-disable-line

  if (!current || !chapter || !cave) return null;

  const isFinale = cave.boss && chapter.crystal;

  const onComplete = (s) => {
    setStars(s);
    setPhase(isFinale ? "crystal" : "reward");
  };

  const onPick = async (treasure) => {
    try {
      await saveCave(chapter.id, cave.id, stars, { id: treasure.id, emoji: treasure.emoji, name: treasure.name });
    } catch (e) {
      /* ignore */
    }
    navigate(`/chapter/${chapter.id}`);
  };

  const onCrystalContinue = async () => {
    try {
      const c = chapter.crystal;
      await saveCave(chapter.id, cave.id, stars, { id: c.id, emoji: c.emoji, name: c.name, type: "crystal" });
    } catch (e) {
      /* ignore */
    }
    if (chapter.cliffhanger) setPhase("cliffhanger");
    else navigate(`/chapter/${chapter.id}`);
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,#1E1B4B 0%,#312E81 60%,#4C1D95 100%)" }}>
      <div className="sticky top-0 z-40 flex items-center justify-between px-3 py-2.5 border-b-4 border-violet-400" style={{ background: "#1E1B4BF2" }}>
        <button data-testid="cave-home-btn" onClick={() => { sfx.tap(); navigate(`/chapter/${chapter.id}`); }} className="pop-btn flex items-center justify-center rounded-2xl bg-white/90 border-4 border-violet-300 shadow-[0_4px_0_#A78BFA] active:shadow-none" style={{ width: 50, height: 50 }}>
          <Home className="w-6 h-6 text-slate-700" strokeWidth={2.5} />
        </button>
        <h1 className="font-display text-lg font-black text-violet-100">{cave.title}</h1>
        <div style={{ width: 50 }} />
      </div>

      {phase === "intro" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center text-center px-6 pt-10 no-select">
          <motion.div animate={{ y: [0, -14, 0], rotate: [-6, 6, -6] }} transition={{ repeat: Infinity, duration: 3 }} className="text-7xl mb-4">{cave.boss ? "🐉" : "🕳️"}</motion.div>
          <div className="flex items-center gap-2 mb-2 text-amber-300 font-display font-bold"><span className="text-2xl">{COMPANION.emoji}</span> Lumi says:</div>
          <p className="font-display text-xl font-bold text-indigo-100 max-w-md mb-8">{cave.beat}</p>
          <button data-testid="cave-enter-btn" onClick={() => { sfx.unlock(); sfx.tap(); setPhase("play"); }} className="pop-btn inline-flex items-center gap-2 rounded-full bg-amber-400 text-indigo-900 font-display text-xl font-black px-8 py-4 border-4 border-white shadow-[0_6px_0_#B45309] active:shadow-none">
            Enter the Cave! <ArrowRight strokeWidth={3} />
          </button>
        </motion.div>
      )}

      {phase === "play" && (
        <CavePlayer cave={cave} band={getBand(current.age_band)} onComplete={onComplete} />
      )}

      {phase === "reward" && <RewardPick stars={stars} onPick={onPick} />}

      {phase === "crystal" && <CrystalAward crystal={chapter.crystal} stars={stars} onContinue={onCrystalContinue} />}

      {phase === "cliffhanger" && (
        <Cliffhanger
          cliffhanger={chapter.cliffhanger}
          onUnlock={() => navigate("/parent")}
          onBack={() => navigate(`/chapter/${chapter.id}`)}
        />
      )}
    </div>
  );
}
